<template>
  <model-form
    ref="form"
    :model="registerButton"
    :submodel="true"
    modal-type="modal"
    :embedded-in-model-table="false"
  >
    <q-card-section>
      <div class="row q-gutter-md">
        <q-select
          v-model="registerButton.type"
          class="col"
          label="Type"
          :options="registerButton.fieldOptions('type')"
          emit-value
          map-options
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.ITEM"
          v-model="registerButton.item"
          use-input
          hide-selected
          fill-input
          input-debounce="0"
          label="Item"
          :options="itemOptions"
          emit-value
          map-options
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
          @filter="itemFilterFunction"
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.ITEM_VARIATION"
          v-model="registerButton.itemVariation"
          use-input
          hide-selected
          fill-input
          input-debounce="0"
          label="Item Variation"
          :options="itemVariationOptions"
          emit-value
          map-options
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
          @filter="itemVariationFilterFunction"
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.ITEM_PACKAGE"
          v-model="registerButton.itemPackage"
          use-input
          hide-selected
          fill-input
          input-debounce="0"
          label="Package"
          :options="packageOptions"
          emit-value
          map-options
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
          @filter="packageFilterFunction"
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.VOUCHER_SALE"
          v-model="registerButton.voucherType"
          class="col"
          label="Voucher Types"
          :options="$_.filter(voucherTypes.data, { forSale: true })"
          option-label="name"
          emit-value
          :display-value="registerButton.voucherType ? registerButton.voucherType.name : ''"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.VOUCHER_SERVICE_ISSUE"
          v-model="registerButton.voucherType"
          class="col"
          label="Voucher Types"
          :options="$_.filter(voucherTypes.data, { serviceIssue: true })"
          option-label="name"
          :display-value="registerButton.voucherType ? registerButton.voucherType.name : ''"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.APPLY_VOUCHER"
          v-model="registerButton.voucherType"
          class="col"
          label="Voucher Types"
          :options="voucherTypes.data"
          option-label="name"
          emit-value
          :display-value="registerButton.voucherType ? registerButton.voucherType.name : ''"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.VOUCHER_PACKAGE"
          v-model="registerButton.voucherPackage"
          class="col"
          label="Voucher Packages"
          :options="voucherPackages.data"
          emit-value
          option-label="name"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <q-select
          v-if="registerButton.type === BUTTON_TYPES.DISCOUNT"
          v-model="registerButton.discount"
          class="col"
          label="Discount"
          :options="discounts.data"
          option-label="name"
          :display-value="registerButton.discount ? registerButton.discount.displayName : ''"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-if="registerButton.type === BUTTON_TYPES.MANUAL_DISCOUNT"
          v-model.trim="registerButton.buttonOptions.button_name"
          class="col"
          label="Button Name"
          bottom-slots
        />
      </div>
      <div
        v-if="registerButton.type === BUTTON_TYPES.MANUAL_DISCOUNT"
        class="row q-gutter-md"
      >
        <q-select
          v-model="registerButton.discountIds"
          :multiple="true"
          :options="discounts.data"
          map-options
          class="col"
          emit-value
          option-value="id"
          stack-label
          label="Discounts"
          use-chips
          bottom-slots
        />
      </div>
      <div
        v-if="registerButton.type === BUTTON_TYPES.GIFT_CARD"
        class="row q-col-gutter-sm"
      >
        <q-input
          v-model.trim="registerButton.buttonOptions.button_name"
          class="col-6"
          label="Button Name"
          focus
          bottom-slots
        >
          <q-tooltip>
            This will be used for the name of the button displayed on the register grid.
          </q-tooltip>
        </q-input>
        <q-select
          v-model="registerButton.buttonOptions.gift_card_type"
          class="col-6"
          label="Gift Card Type"
          :options="$mh.asOptions(GIFT_CARD_SALE_TYPES.ALL)"
          map-options
          emit-value
          bottom-slots
        />
        <q-input
          v-model.number="registerButton.buttonOptions.default_amount"
          class="col-6"
          label="Amount"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          step="1"
          :min="registerButton.buttonOptions.gift_card_type === GIFT_CARD_SALE_TYPES.CUSTOM ? 0 : 1"
          :rules="[
            val => $_.gte(val, 0) || 'Field must be greater than 0',
          ]"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the gift card's amount value when clicking the button.
          </q-tooltip>
        </q-input>
        <template v-if="registerButton.buttonOptions.gift_card_type === GIFT_CARD_SALE_TYPES.BONUS">
          <q-input
            v-model.number="registerButton.buttonOptions.gift_card_bonus_amount"
            class="col-6"
            label="Bonus Gift Card Amount"
            type="number"
            step="5"
            min="0"
            :rules="[val => $_.gt(val, 0) || 'Field must be greater than 0']"
            bottom-slots
          />
          <q-select
            v-model="registerButton.discountId"
            class="col-6"
            label="Bonus Gift Card Discount"
            :options="giftCardDiscounts"
            option-label="name"
            option-value="id"
            map-options
            emit-value
            clearable
            bottom-slots
          />
        </template>
      </div>
      <div class="row q-gutter-md">
        <q-toggle
          v-model="registerButton.buttonOptions.requires_authorization"
          class="col"
          label="Requires Admin Authorization"
          bottom-slots
        >
          <q-tooltip>
            Requires an Admin to allow the usage of this button.
          </q-tooltip>
        </q-toggle>
      </div>
      <div
        v-if="registerButton.type === BUTTON_TYPES.ITEM || registerButton.type === BUTTON_TYPES.ITEM_VARIATION"
        class="row q-gutter-md"
      >
        <q-toggle
          v-model="registerButton.overrideSkipDetailDialog"
          class="col"
          label="Override Skip Detail Dialog"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            Value is set gobally for {{ registerButton.item ? registerButton.item.name : 'this Item' }}. Choose to override for this button.
          </q-tooltip>
        </q-toggle>
        <q-checkbox
          v-if="!registerButton.overrideSkipDetailDialog"
          :model-value="registerButton.item ? registerButton.item.skipDetailDialog : false"
          disable
          class="col"
          label="Skip Detail Dialog"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
        <q-checkbox
          v-if="registerButton.overrideSkipDetailDialog"
          v-model="registerButton.skipDetailDialog"
          class="col"
          label="Skip Detail Dialog"
          :disable="!registerButton.overrideSkipDetailDialog"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        />
      </div>
      <div
        v-if="registerButton.type === BUTTON_TYPES.DISCOUNT && registerButton.discount.data.name || registerButton.type === BUTTON_TYPES.MANUAL_DISCOUNT"
        class="row q-gutter-md"
      >
        <q-toggle
          v-model="registerButton.overrideImage"
          class="col"
          label="Override Default Image"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            This will override the default image, as defined in the site configuration.
          </q-tooltip>
        </q-toggle>
        <image-uploader
          v-if="registerButton.overrideImage"
          v-model="registerButton.image"
          class="col"
          :image-url="registerButton.imageUrl"
          :model="registerButton"
          min-height="118px"
        />
      </div>
      <div
        v-if="registerButton.type === BUTTON_TYPES.MISC_ITEM"
        class="row items-center q-col-gutter-sm"
      >
        <q-input
          v-model.trim="registerButton.buttonOptions.button_name"
          class="col-6"
          label="Button Name"
          focus
          bottom-slots
        >
          <q-tooltip>
            This will be used for the name of the button displayed on the register grid, instead of 'Miscellaneous'. This is not printed on prep slips.
          </q-tooltip>
        </q-input>
        <q-input
          v-model.number="registerButton.buttonOptions.default_amount"
          class="col-6"
          label="Default Amount"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          step="0.01"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the misc item's amount value when clicking the button.
          </q-tooltip>
        </q-input>
        <q-input
          v-model.trim="registerButton.buttonOptions.default_description"
          class="col-12"
          label="Default Description"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the misc item's description when clicking the button. This is also printed to prep slips.
          </q-tooltip>
        </q-input>
        <q-checkbox
          v-model="registerButton.buttonOptions.default_taxable"
          class="col-4 q-pb-md"
          label="Default Taxable"
          :disable="registerButton.buttonOptions.force_tax"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the taxable checkbox when clicking the button.
          </q-tooltip>
        </q-checkbox>
        <q-select
          v-if="registerButton.buttonOptions.default_taxable"
          v-model="registerButton.buttonOptions.default_tax_ids"
          class="col-8"
          :label="$t('pricing.tax')"
          stack-label
          :options="taxes.data"
          option-value="id"
          map-options
          emit-value
          multiple
          clearable
          :rules="[val => (!!val && val.length > 0) || 'Field is required']"
          bottom-slots
        >
          <template #selected-item="scope">
            <q-chip
              v-if="scope.opt.name"
              removable
              dense
              :tabindex="scope.tabindex"
              :label="scope.opt.name"
              @remove="scope.removeAtIndex(scope.index)"
            />
          </template>
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                {{ scope.opt.name }}
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <div
          v-else
          class="col-6"
        />
        <q-checkbox
          v-model="registerButton.buttonOptions.force_tax"
          class="col-6 q-pb-md"
          label="Force Tax"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            Ensures Tax is Always Applied
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-model="registerButton.buttonOptions.default_print"
          class="col-6 q-pb-md"
          label="Default Print"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the print checkbox when clicking the button.
          </q-tooltip>
        </q-checkbox>
        <q-select
          v-if="registerButton.buttonOptions.default_print"
          v-model="registerButton.buttonOptions.default_print_location_id"
          class="col-6"
          label="Default Print Location"
          :options="$_.map(locations.data, location => { return { id: location.id, label: location.name } })"
          option-value="id"
          map-options
          emit-value
          :rules="[val => !registerButton.buttonOptions.default_print || val !== null || 'Field is required']"
        />
        <div
          v-else
          class="col-6"
        />
      </div>
      <div
        v-if="registerButton.type === BUTTON_TYPES.DONATION"
        class="row items-center q-col-gutter-sm"
      >
        <q-input
          v-model.trim="registerButton.buttonOptions.button_name"
          class="col-6"
          label="Button Name"
          focus
          bottom-slots
        >
          <q-tooltip>
            This will be used for the name of the button displayed on the register grid, instead of 'Donation'. This is not printed on prep slips.
          </q-tooltip>
        </q-input>
        <q-input
          v-model.number="registerButton.buttonOptions.default_amount"
          class="col-6"
          label="Default Amount"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          step="0.01"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the donation's amount value when clicking the button.
          </q-tooltip>
        </q-input>
        <q-input
          v-model.trim="registerButton.buttonOptions.default_description"
          class="col-12"
          label="Default Description"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the donation's description when clicking the button. This is also printed to prep slips.
          </q-tooltip>
        </q-input>
        <q-input
          v-if="currentSite.sendToDonorPerfect"
          v-model.trim="registerButton.buttonOptions.donor_perfect_campaign_code"
          class="col-12"
          label="DonorPerfect Campaign Code"
          bottom-slots
        >
          <q-tooltip>
            The campaign code in DonorPerfect that this button should correspond to.
          </q-tooltip>
        </q-input>
        <q-checkbox
          v-model="registerButton.buttonOptions.default_taxable"
          class="col-6 q-pb-md"
          label="Default Taxable"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            This will be used to prefill the taxable checkbox when clicking the button.
          </q-tooltip>
        </q-checkbox>
        <q-select
          v-if="registerButton.buttonOptions.default_taxable"
          v-model="registerButton.buttonOptions.default_tax_id"
          class="col-6"
          :label="'Default ' + $t('pricing.tax')"
          :options="taxes.data"
          option-label="name"
          option-value="id"
          map-options
          emit-value
          bottom-slots
          :rules="[val => !registerButton.buttonOptions.default_taxable || val !== null || 'Field is required']"
        />
        <div
          v-else
          class="col-6"
        />
      </div>
    </q-card-section>
  </model-form>
</template>

<script>

import { TYPES as GIFT_CARD_SALE_TYPES } from 'src/models/gift-card/GiftCardSale'
import { BUTTON_TYPES } from 'src/models/register/RegisterButton'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

export default {
  name: 'RegisterButtonForm',
  components: {
    ModelForm,
    ImageUploader
  },
  configuration: ['currentCircuit', 'currentSite', 'items', 'itemVariations', 'voucherTypes', 'discounts', 'itemPackages', 'voucherPackages', 'taxes', 'locations'],
  models: {
    registerButton: {
      voucherPackage: 'default',
      itemPackage: 'default',
      item: {
        itemClass: 'default'
      },
      itemVariation: {
        item: 'default'
      },
      voucherType: 'default',
      discount: 'default',
      discounts: 'default'
    }
  },
  data () {
    return {
      itemSearchValue: null,
      itemVariationSearchValue: null,
      packageSearchValue: null
    }
  },
  computed: {
    giftCardDiscounts () {
      return _.filter(this.discounts.data, { type: 'gift-card' })
    },
    itemOptions () {
      const filteredOptions = _.filter(this.items.data, 'isUnarchived')
      const allOptions = _.map(filteredOptions, model => { return { value: model, label: _([model.itemClass?.name, model.name]).compact().join('-') } })
      if (!this.itemSearchValue) return allOptions

      const searchTerms = this.itemSearchValue.toLowerCase().split(' ')

      const filteredValues = allOptions.filter(option => {
        const item = option.value
        return _.every(searchTerms, term => {
          return item.searchString.includes(term)
        })
      })
      return filteredValues
    },
    itemVariationOptions () {
      const allOptions = _.map(this.itemVariations.data, model => { return { value: model, label: _([model.item?.itemClass?.name, model.item.name, model.name]).compact().join('-') } })
      if (!this.itemVariationSearchValue) return allOptions

      const searchTerms = this.itemVariationSearchValue.toLowerCase().split(' ')

      const filteredValues = allOptions.filter(option => {
        const itemVariation = option.value
        return _.every(searchTerms, term => {
          return itemVariation.searchString.includes(term)
        })
      })
      return filteredValues
    },
    packageOptions () {
      const allOptions = _.map(_.filter(this.itemPackages.data, ip => ip.itemOnlyPackage), model => { return { value: model, label: model.name } })
      if (!this.packageSearchValue) return allOptions

      const searchTerms = this.packageSearchValue.toLowerCase().split(' ')

      const filteredValues = allOptions.filter(option => {
        const itemPackage = option.value
        return _.every(searchTerms, term => {
          return itemPackage.name.includes(term)
        })
      })
      return filteredValues
    }
  },
  watch: {
    'registerButton.buttonOptions.gift_card_package_count' () {
      if (_.isNil(this.registerButton.buttonOptions.gift_card_package_count)) return

      if (this.registerButton.buttonOptions.gift_card_package_count > this.registerButton.buttonOptions.gift_card_package_amounts.length) {
        const newElements = _.times(
          this.registerButton.buttonOptions.gift_card_package_count - this.registerButton.buttonOptions.gift_card_package_amounts.length,
          () => 10
        )
        this.registerButton.buttonOptions.gift_card_package_amounts = _.concat(this.registerButton.buttonOptions.gift_card_package_amounts, newElements)
      } else if (this.registerButton.buttonOptions.gift_card_package_count < this.registerButton.buttonOptions.gift_card_package_amounts.length) {
        this.registerButton.buttonOptions.gift_card_package_amounts = _.slice(this.registerButton.buttonOptions.gift_card_package_amounts, 0, this.registerButton.buttonOptions.gift_card_package_count)
      }
    }
  },
  created () {
    _.extend(this, { BUTTON_TYPES, GIFT_CARD_SALE_TYPES })
  },
  methods: {
    show (model, initialData) {
      this.$refs.form.show(model, initialData)
    },
    setCollection (collection) {
      this.$refs.form.model.setParent(collection)
    },
    itemFilterFunction (val, update, abort) {
      update(() => {
        this.itemSearchValue = val.toLowerCase()
      })
    },
    itemVariationFilterFunction (val, update, abort) {
      update(() => {
        this.itemVariationSearchValue = val.toLowerCase()
      })
    },
    packageFilterFunction (val, update, abort) {
      update(() => {
        this.packageSearchValue = val.toLowerCase()
      })
    }
  }
}

</script>
